import { useState, Suspense, useEffect } from 'react';
import { Tag, Spin, List, Modal, theme, Avatar, Button, Dropdown } from 'antd';
import {
  UserOutlined,
  SwapOutlined,
  LogoutOutlined,
  TeamOutlined,
  StopOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import ImpersonateModal from './ImpersonateModal';

import { useAuth, signOut } from '../../AuthContext';
import { getRetailAccessToken, listAccounts, switchAccounts } from '../../services/users';

import type { Account } from '../../types/user';

function AccountsList({ onSelect }: { onSelect: (accountId: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);

  useEffect(() => {
    listAccounts().then((data) => {
      setAccounts(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <Spin tip='Loading accounts...' />;

  return (
    <List
      dataSource={accounts}
      renderItem={(account) => (
        <List.Item>
          <Button type='link' onClick={() => onSelect(account.accountId)}>
            {account.accountName}{' '}
            <Tag
              color='blue'
              style={{
                borderRadius: '16px',
                marginLeft: 8,
                padding: '0 8px',
                fontSize: 12,
              }}
            >
              {account.accountType}
            </Tag>
          </Button>
        </List.Item>
      )}
    />
  );
}

function UserDropdown() {
  const { user, refresh } = useAuth();
  const [accountsModalVisible, setAccountsModalVisible] = useState(false);
  const [impersonateModalVisible, setImpersonateModalVisible] = useState(false);

  const { token } = theme.useToken();

  const isGoogleUser = !!user?.picture;
  const DEFAULT_AVATAR_URL =
    'https://www.gstatic.com/images/branding/product/1x/avatar_circle_blue_512dp.png';

  const handleAccountSwitch = async (accountId: string) => {
    await switchAccounts(accountId);
    await refresh();
  };

  const isImpersonating = !!localStorage.getItem('overrideAccessToken');

  const menuItems = [
    {
      key: 'user-name',
      label: (
        <strong style={{ color: token.colorText }}>
          {user?.firstName} {user?.lastName}
        </strong>
      ),
      disabled: true,
    },
    {
      key: 'user-email',
      label: <span style={{ color: token.colorText }}>{user?.email}</span>,
      disabled: true,
    },
    ...(user?.role || user?.accountName
      ? [
          {
            key: 'user-role-account',
            label: (
              <span style={{ color: token.colorText }}>
                {user.accountName ? `${user.accountName} ` : ''}
              </span>
            ),
            disabled: true,
          },
        ]
      : []),
    { type: 'divider' as const },
    ...(!isImpersonating &&
    (user?.hasMultipleAccounts || user.email.toLowerCase().includes('@hexacoder.com'))
      ? [
          {
            key: 'account-switch',
            icon: <SwapOutlined />,
            label: 'Switch Accounts',
            onClick: () => setAccountsModalVisible(true),
          },
        ]
      : []),
    ...(user?.role == 'superadmin'
      ? [
          {
            key: 'impersonate',
            icon: <TeamOutlined />,
            label: 'Impersonate User',
            onClick: () => setImpersonateModalVisible(true),
          },
          {
            key: 'impersonate-retail',
            icon: <ShopOutlined />,
            label: 'Impersonate Retail',
            onClick: () => {
              getRetailAccessToken().then((t) => {
                localStorage.setItem('overrideAccessToken', t.token);
                window.location.reload();
              });
            },
          },
        ]
      : []),

    {
      key: 'signout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: signOut,
    },
  ];

  if (isImpersonating) {
    menuItems.push({
      key: 'stop-impersonation',
      icon: <StopOutlined />,
      label: 'Stop Impersonation',
      onClick: () => {
        localStorage.removeItem('overrideAccessToken');
        window.location.reload();
      },
    });
  }

  return (
    <>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement='bottomRight'>
        {isGoogleUser ? (
          <img
            src={user.picture || DEFAULT_AVATAR_URL}
            alt='avatar'
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              cursor: 'pointer',
              alignSelf: 'center',
            }}
            referrerPolicy='no-referrer'
          />
        ) : (
          <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
        )}
      </Dropdown>

      <Modal
        title='Switch Accounts'
        open={accountsModalVisible}
        onCancel={() => setAccountsModalVisible(false)}
        footer={null}
      >
        <Suspense fallback={<Spin tip='Loading accounts...' />}>
          <AccountsList
            onSelect={(id) => {
              setAccountsModalVisible(false);
              handleAccountSwitch(id);
            }}
          />
        </Suspense>
      </Modal>
      <ImpersonateModal
        open={impersonateModalVisible}
        onClose={() => setImpersonateModalVisible(false)}
      />
    </>
  );
}

export default UserDropdown;
